import { useCallback, useMemo, useState } from 'react';
import { SessionImageDTO } from '../generated';
import { useSelfServiceSocket } from '../useWebSocket';
import { AttributeChipData } from '../../components/Prediction/types';
import { MaterialClassification } from '../queries/materialClassification';
import { shouldDisplayEdgeEstimation, shouldDisplaySoilClassification } from '../../components/Prediction/utils';
import { useSelfServiceMaterialClassification } from '../queries/selfService/selfServiceMaterialClassification';
import { UseQueryResult } from '@tanstack/react-query';
import { ApiError } from '../../components/ApiLoadingGuard/ApiLoadingGuard';
import { useSelfServiceSoilClassification } from '../queries/selfService/selfServiceSoilClassification';
import { useSelfServiceEdgeEstimation } from '../queries/selfService/selfServiceEdgeEstimation';
import { useSelfServiceImage } from '../queries/selfService/selfServiceImage';
import { useQueryClient } from '@tanstack/react-query';
import { selfServiceSessionImagesKeys } from '../queries/selfService/selfServiceImages';

export type ResourceStatus = 'not-started' | 'started' | 'finished' | 'error';
export type ConnectionStatus = 'connected' | 'disconnected' | 'connecting';

export interface SelfServiceEventState {
  connectionStatus: ConnectionStatus;
  recordingStatus: ResourceStatus;
  imageStatus: ResourceStatus;
  edgeSizeStatus: ResourceStatus;
  materialTypeStatus: ResourceStatus;
  soilClassificationStatus: ResourceStatus;
  image: UseQueryResult<SessionImageDTO, ApiError>;
  edgeSize: UseQueryResult<AttributeChipData[], ApiError>;
  materialType: UseQueryResult<MaterialClassification, ApiError>;
  soilClassification: UseQueryResult<AttributeChipData[], ApiError>;
  observedCaptureCount: number;
}

export function useOcInsightsEvents(
  jobsiteId: string,
  sessionId: string,
  selectedCaptureStartAt: string | null,
  selectedImageIndex: number | null,
  predictionQueriesEnabled: boolean
): SelfServiceEventState {
  // Dependencies:
  // - image: needs recording
  // - edgeSize: needs recording
  // - materialType: needs recording
  // NOTE: this is not quite modeled correctly, as we get predictions as a child of the image.
  // So we wait for the image to be ready before fetching edgeSize and materialType.
  const queryClient = useQueryClient();
  const [connectionStatus, setConnectionStatus] = useState<ConnectionStatus>('connecting');
  const [uploadedCaptures, setUploadedCaptures] = useState<string[]>([]);
  const [stitchedCaptures, setStitchedCaptures] = useState<string[]>([]);
  const [materialCaptures, setMaterialCaptures] = useState<string[]>([]);
  const [edgeCaptures, setEdgeCaptures] = useState<string[]>([]);
  const [soilCaptures, setSoilCaptures] = useState<string[]>([]);

  const recordingStatus = uploadedCaptures.length > 0 ? 'finished' : 'not-started';
  const imageStatus = getCaptureStatus(stitchedCaptures, uploadedCaptures, selectedCaptureStartAt);
  const materialTypeStatus = getPredictionStatus(materialCaptures, selectedCaptureStartAt, imageStatus);
  const edgeSizeStatus = getPredictionStatus(edgeCaptures, selectedCaptureStartAt, imageStatus);
  const soilClassificationStatus = getPredictionStatus(soilCaptures, selectedCaptureStartAt, imageStatus);

  const image = useSelfServiceImage({ jobsiteId, sessionId, imageIndex: selectedImageIndex, imageStatus });

  const materialType = useSelfServiceMaterialClassification({
    jobsiteId,
    sessionId,
    imageIndex: selectedImageIndex,
    materialTypeStatus,
    imageStatus,
    enabled: predictionQueriesEnabled,
  });

  const displayEdgeEstimation = shouldDisplayEdgeEstimation(materialType.data?.rawPredictions);

  const edgeSize = useSelfServiceEdgeEstimation({
    jobsiteId,
    sessionId,
    imageIndex: selectedImageIndex,
    imageStatus,
    edgeSizeStatus,
    displayEdgeEstimation,
  });

  const displaySoilClassification = shouldDisplaySoilClassification(materialType.data?.rawPredictions);

  const soilClassification = useSelfServiceSoilClassification({
    jobsiteId,
    sessionId,
    imageIndex: selectedImageIndex,
    imageStatus,
    soilClassificationStatus,
    enabled: predictionQueriesEnabled && displaySoilClassification,
  });

  const imageListQueryKey = useMemo(
    () => selfServiceSessionImagesKeys.list({ jobsiteId, sessionId }),
    [jobsiteId, sessionId]
  );

  const handleMessage = useCallback(
    (data: string) => {
      try {
        const event = JSON.parse(data);
        const captureStartAt = normalizeCaptureStartAt(event.capture_start_at);

        // Message tracking
        if (!captureStartAt) return;

        switch (event.type) {
          case 'datapoint_uploaded':
            setUploadedCaptures((prev) => addUnique(prev, captureStartAt));
            queryClient.invalidateQueries({ queryKey: imageListQueryKey });
            break;
          case 'stitching_finished':
            setStitchedCaptures((prev) => addUnique(prev, captureStartAt));
            queryClient.invalidateQueries({ queryKey: imageListQueryKey });
            break;
          case 'classification_finished':
            setMaterialCaptures((prev) => addUnique(prev, captureStartAt));
            break;
          case 'edge_estimation_finished':
            setEdgeCaptures((prev) => addUnique(prev, captureStartAt));
            break;
          case 'soil_classification_finished':
            setSoilCaptures((prev) => addUnique(prev, captureStartAt));
            break;
          default:
            console.warn('Unknown message type:', event.type);
            break;
        }
      } catch (error) {
        console.error('Error parsing WebSocket message:', error);
      }
    },
    [imageListQueryKey, queryClient]
  );

  useSelfServiceSocket(jobsiteId, sessionId, {
    onConnect: () => setConnectionStatus('connected'),
    onDisconnect: () => setConnectionStatus('disconnected'),
    onConnectError: () => setConnectionStatus('disconnected'),
    onEvent: handleMessage,
  });

  return {
    connectionStatus,
    recordingStatus,
    imageStatus,
    edgeSizeStatus,
    materialTypeStatus,
    soilClassificationStatus,
    image,
    edgeSize,
    materialType,
    soilClassification,
    observedCaptureCount: countUniqueCaptures(uploadedCaptures, stitchedCaptures),
  };
}

function addUnique(values: string[], value: string): string[] {
  return values.includes(value) ? values : [...values, value];
}

export function normalizeCaptureStartAt(value: unknown): string | null {
  if (!value) return null;

  const parsed = new Date(String(value));
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
}

function countUniqueCaptures(...captureLists: string[][]): number {
  return new Set(captureLists.flat()).size;
}

function getCaptureStatus(
  stitchedCaptures: string[],
  uploadedCaptures: string[],
  selectedCaptureStartAt: string | null
): ResourceStatus {
  if (selectedCaptureStartAt) {
    if (stitchedCaptures.includes(selectedCaptureStartAt)) return 'finished';
    if (uploadedCaptures.includes(selectedCaptureStartAt)) return 'started';
    return 'finished';
  }

  if (stitchedCaptures.length > 0) return 'finished';
  if (uploadedCaptures.length > 0) return 'started';
  return 'not-started';
}

function getPredictionStatus(
  predictionCaptures: string[],
  selectedCaptureStartAt: string | null,
  imageStatus: ResourceStatus
): ResourceStatus {
  if (selectedCaptureStartAt) {
    if (predictionCaptures.includes(selectedCaptureStartAt)) return 'finished';
    return imageStatus === 'finished' ? 'started' : 'not-started';
  }

  return predictionCaptures.length > 0 ? 'finished' : 'not-started';
}
